import React from 'react'
import useSWRMutation from 'swr/mutation'
import { postFetcher } from 'api/swr'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ReloadIcon } from '@radix-ui/react-icons'
import Selector from 'components/ui/Selector'
import MyPagination from 'components/ui/MyPagination'
import { X } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { CreateTagButton } from 'components/create-tag-dialog'

const levelOptions = [
  { code: 2, name: 'Level 2' },
  { code: 3, name: 'Level 3' },
]

const orderOptions = [
  { code: 'frameCount', name: 'frameCount' },
  { code: 'updateTime', name: 'updateTime' },
]

const FilterLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="shrink-0 text-sm font-medium">
    {children}
  </label>
)

const DataTableToolbar = ({
  mutate: refetch,
  level,
  onLevelChange,
  order,
  onOrderChange,
  after,
  afters,
  nextAfter,
  goAfter,
  search,
  onSearchChange,
}) => {
  return (
    <div className="mb-2 flex items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <MyPagination
          nextAfter={nextAfter}
          after={after}
          goAfter={goAfter}
          afters={afters}
        />
        <Button variant="outline" onClick={() => refetch()}>
          <ReloadIcon className="mr-2 h-4 w-4" />
          Refetch
        </Button>
        <div className="flex items-center gap-2">
          <FilterLabel htmlFor="taglibs-level">Level:</FilterLabel>
          <div className="relative">
            <Selector
              id="taglibs-level"
              datas={levelOptions}
              labelName="level"
              value={level}
              update={onLevelChange}
              width="w-[180px]"
              placeholder="All"
            />
            {level != null ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                title="Clear level"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onLevelChange(null)
                }}
                className="absolute right-8 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <FilterLabel htmlFor="taglibs-order">Order:</FilterLabel>
          <Selector
            id="taglibs-order"
            datas={orderOptions}
            labelName="order"
            value={order}
            update={onOrderChange}
            width="w-[180px]"
          />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <div className="relative">
          <Input
            id="taglibs-search"
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search all tags"
            className="w-56"
          />
          {search ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title="Clear search"
              onClick={() => onSearchChange('')}
              className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
        <CreateTagButton />
        <div className="hidden">
          <DataTableBackfillI18n />
        </div>
      </div>
    </div>
  )
}

export default DataTableToolbar

const DataTableBackfillI18n = () => {
  const { toast } = useToast()
  const { trigger, isMutating } = useSWRMutation(
    '/taglibs/i18n/backfill',
    postFetcher,
  )

  const onClick = async () => {
    try {
      const result = await trigger({})
      toast({
        title: 'Backfill queued',
        description: `Queued ${result?.queued ?? 0} tags`,
      })
    } catch (error) {
      toast({
        title: 'Backfill failed',
        description: error?.message || 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  return (
    <Button variant="outline" onClick={onClick} disabled={isMutating}>
      {isMutating ? 'Queueing...' : 'Translate missing'}
    </Button>
  )
}
